import { AdvPopupType } from "@/types/components/advPopupType";
import Modal from "../Modal";
import { PropsWithChildren, SVGProps, memo, useEffect, useMemo, useState } from "react";
import { Link } from "@inertiajs/react";

const stripWww = (hostname: string) => hostname.toLowerCase().replace(/^www\./, "");

/**
 * Normalizes CMS links so Inertia never XHR-visits a cross-origin absolute URL
 * (e.g. https://kusocinskiego.pl/... while the user is on www).
 */
function resolvePopupHref(link?: string, externalLink?: boolean): {
    href: string;
    useExternalAnchor: boolean;
} | null {
    if (!link) {
        return null;
    }

    const trimmed = link.trim();

    if (externalLink) {
        return { href: trimmed, useExternalAnchor: true };
    }

    try {
        const isAbsolute = /^https?:\/\//i.test(trimmed);
        if (!isAbsolute) {
            return { href: trimmed.startsWith("/") ? trimmed : `/${trimmed}`, useExternalAnchor: false };
        }

        const url = new URL(trimmed);
        const isSameSite =
            typeof window !== "undefined" &&
            stripWww(url.hostname) === stripWww(window.location.hostname);

        if (isSameSite) {
            return {
                href: `${url.pathname}${url.search}${url.hash}` || "/",
                useExternalAnchor: false,
            };
        }

        return { href: trimmed, useExternalAnchor: true };
    } catch {
        return { href: trimmed, useExternalAnchor: true };
    }
}

const AdvPopup = ({ expireAt, externalLink, link, photo }: AdvPopupType) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const resolved = useMemo(
        () => resolvePopupHref(link, externalLink),
        [link, externalLink]
    );

    useEffect(() => {
        if (expireAt) {
            const expireAtDate = new Date(expireAt);
            const currentDate = new Date();

            if (expireAtDate > currentDate) {
                setIsOpen(true);
            }
        }
    }, [expireAt]);

    const LineMdClose = (props: SVGProps<SVGSVGElement>) => {
        return (
            <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" {...props}>
                <path
                    fill="none"
                    stroke="currentColor"
                    strokeDasharray={12}
                    strokeDashoffset={12}
                    strokeLinecap="round"
                    strokeWidth={2}
                    d="M12 12L19 19M12 12L5 5M12 12L5 19M12 12L19 5"
                >
                    <animate fill="freeze" attributeName="stroke-dashoffset" dur="1s" values="12;0"></animate>
                </path>
            </svg>
        );
    };

    const AdvPopupLink = ({
        href,
        useExternalAnchor,
        children,
    }: PropsWithChildren & { href: string; useExternalAnchor: boolean }) => {
        if (useExternalAnchor) {
            return (
                <a
                    className="cursor-pointer"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Przejdź do strony ${href}`}
                >
                    {children}
                </a>
            );
        }

        return (
            <Link href={href} className="cursor-pointer" title={`Przejdź do podstrony ${href}`}>
                {children}
            </Link>
        );
    };

    return (
        <div>
            <Modal show={isOpen} onClose={() => setIsOpen(false)} withoutBg>
                <div className="relative">
                    <button
                        onClick={() => setIsOpen(false)}
                        className="absolute z-10 bg-white -top-8 md:-right-8 -right-3 rounded-full flex justify-center items-center md:p-3 p-1 outline-none text-primary border-[4px] border-primary"
                    >
                        <LineMdClose />
                    </button>

                    {resolved ? (
                        <AdvPopupLink href={resolved.href} useExternalAnchor={resolved.useExternalAnchor}>
                            <img src={photo} alt="Zdjęcie" />
                        </AdvPopupLink>
                    ) : (
                        <img src={photo} alt="Zdjęcie" />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default memo(AdvPopup);
